import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AlertTriangle, Clock3, Filter, RefreshCcw } from "lucide-react";

import { getCommandCenterDashboard, getDashboard, getGamificationDashboard, getProjectCommandCenter } from "@/lib/api";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { toast } from "@/components/ui/sonner";

const bandConfig = {
  healthy: {
    label: "Healthy",
    range: "(80+)",
    textColor: "var(--ok-700)",
    dotClass: "dot dot-ok",
    chipClass: "chip chip-ok",
    stroke: "#10B981",
  },
  watch: {
    label: "Watch",
    range: "(65–79)",
    textColor: "var(--warn-700)",
    dotClass: "dot dot-warn",
    chipClass: "chip chip-warn",
    stroke: "#F59E0B",
  },
  at_risk: {
    label: "At risk",
    range: "(50–64)",
    textColor: "var(--risk-700)",
    dotClass: "dot dot-risk",
    chipClass: "chip chip-risk",
    stroke: "#F97316",
  },
  critical: {
    label: "Critical",
    range: "(<50)",
    textColor: "var(--crit-700)",
    dotClass: "dot dot-crit",
    chipClass: "chip chip-crit",
    stroke: "#EF4444",
  },
};

const toDate = (value) => {
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const initials = (value = "") => {
  const clean = value.trim();
  if (!clean) return "NA";
  const parts = clean.split(" ");
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
};

const sparklinePoints = (score = 0) => {
  const safe = Math.max(20, Math.min(95, Number(score || 0)));
  return [
    Math.max(10, safe - 24),
    Math.max(15, safe - 12),
    Math.max(20, safe - 6),
    safe,
  ];
};

const trendPathFromPoints = (points = []) => {
  if (!points.length) return "";
  const maxX = 88;
  const maxY = 22;
  const step = points.length > 1 ? maxX / (points.length - 1) : 0;
  return points
    .map((point, index) => {
      const x = Number((index * step).toFixed(1));
      const y = Number((maxY - point / 4).toFixed(1));
      return `${index === 0 ? "M" : "L"}${x},${y}`;
    })
    .join(" ");
};

const monthKeyShift = (baseMonth, delta) => {
  const [yearPart, monthPart] = String(baseMonth).split("-").map(Number);
  const date = new Date(yearPart, monthPart - 1 + delta, 1);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
};

const currentMonthKey = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
};

export default function DashboardPage({ user, onLogout }) {
  const navigate = useNavigate();
  const location = useLocation();

  const [dashboardData, setDashboardData] = useState({ open_tasks: [], projects: [], important_pings: [] });
  const [commandCenter, setCommandCenter] = useState({ overview: {}, client_rows: [], service_rows: [] });
  const [trendMap, setTrendMap] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [filters, setFilters] = useState({ project: "", client: "" });
  const [gamificationData, setGamificationData] = useState(null);
  const [activeBadgeRole, setActiveBadgeRole] = useState("account_manager");

  const loadNinetyDayTrends = async (projects) => {
    if (!projects?.length) {
      setTrendMap({});
      return;
    }

    const baseMonth = currentMonthKey();
    const months = [monthKeyShift(baseMonth, -2), monthKeyShift(baseMonth, -1), baseMonth];

    try {
      const calls = projects.flatMap((project) =>
        months.map((month) =>
          getProjectCommandCenter(project.id, month)
            .then((response) => ({ projectId: project.id, month, health: Number(response.health_score || 0) }))
            .catch(() => ({ projectId: project.id, month, health: null })),
        ),
      );

      const rows = await Promise.all(calls);
      const next = {};
      rows.forEach((row) => {
        if (!next[row.projectId]) next[row.projectId] = {};
        if (row.health !== null) next[row.projectId][row.month] = row.health;
      });

      const ordered = {};
      projects.forEach((project) => {
        const bucket = next[project.id] || {};
        const values = months.map((month) => bucket[month]).filter((value) => typeof value === "number");
        ordered[project.id] = values.length >= 2 ? values : sparklinePoints((commandCenter.client_rows || []).find((r) => r.client_name === project.client_name)?.health_score || 0);
      });

      setTrendMap(ordered);
    } catch (_error) {
      // no-op fallback to static trend values in render
    }
  };

  const loadDashboard = async (activeFilters = filters) => {
    setIsLoading(true);
    try {
      const [dashboardResponse, commandCenterResponse, gamificationResponse] = await Promise.all([
        getDashboard({
          project_query: activeFilters.project || undefined,
          client_query: activeFilters.client || undefined,
        }),
        getCommandCenterDashboard(),
        getGamificationDashboard(),
      ]);
      setDashboardData(dashboardResponse);
      setCommandCenter(commandCenterResponse);
      setGamificationData(gamificationResponse);
      setActiveBadgeRole(gamificationResponse?.role_default || "account_manager");
      await loadNinetyDayTrends(dashboardResponse.projects || []);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadDashboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadGamificationMonth = async (month) => {
    try {
      const response = await getGamificationDashboard({ month });
      setGamificationData(response);
    } catch (error) {
      toast.error(error.message);
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("google_connected") === "true") {
      toast.success("Google Workspace connected successfully.");
      params.delete("google_connected");
      navigate({ pathname: location.pathname, search: params.toString() }, { replace: true });
    }
  }, [location.pathname, location.search, navigate]);

  useEffect(() => {
    if (location.state?.denied) {
      toast.error("You don't have access to this page.");
      navigate(`${location.pathname}${location.search}`, { replace: true, state: {} });
    }
  }, [location.pathname, location.search, location.state, navigate]);

  const now = useMemo(() => {
    const date = new Date();
    const weekNum = Math.ceil((date.getDate() - date.getDay() + 1) / 7) + 1;
    return {
      header: date.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" }),
      weekNum,
    };
  }, []);

  const openTasks = useMemo(() => {
    if (["account_manager", "writer", "designer", "editor"].includes(user.role)) {
      return dashboardData.open_tasks.filter((task) => task.assigned_to === user.email);
    }
    return dashboardData.open_tasks;
  }, [dashboardData.open_tasks, user.email, user.role]);

  const overdueTasks = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return openTasks.filter((task) => {
      const due = toDate(task.deadline);
      return due && due < today;
    });
  }, [openTasks]);

  const dueInWeekTasks = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const weekLater = new Date(today);
    weekLater.setDate(weekLater.getDate() + 7);
    return openTasks.filter((task) => {
      const due = toDate(task.deadline);
      return due && due >= today && due <= weekLater;
    });
  }, [openTasks]);

  const taskStats = useMemo(
    () => ({
      overdue: overdueTasks.length,
      dueWeek: dueInWeekTasks.length,
      onTrack: Math.max(0, openTasks.length - overdueTasks.length),
    }),
    [dueInWeekTasks.length, openTasks.length, overdueTasks.length],
  );

  const groupedBandRows = useMemo(() => {
    const buckets = { healthy: [], watch: [], at_risk: [], critical: [] };
    (commandCenter.client_rows || []).forEach((row) => {
      const key = row.health_status || "watch";
      if (!buckets[key]) buckets[key] = [];
      buckets[key].push(row);
    });
    return buckets;
  }, [commandCenter.client_rows]);

  const projectOverdueMap = useMemo(() => {
    const map = new Map();
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    dashboardData.open_tasks.forEach((task) => {
      const due = toDate(task.deadline);
      if (due && due < today) {
        map.set(task.project_id, (map.get(task.project_id) || 0) + 1);
      }
    });
    return map;
  }, [dashboardData.open_tasks]);

  const upcomingDeliverables = useMemo(
    () => [...openTasks].sort((a, b) => String(a.deadline || "").localeCompare(String(b.deadline || ""))).slice(0, 4),
    [openTasks],
  );

  const streakSummary = gamificationData?.streak || {};
  const monthOptions = gamificationData?.month_options || [];
  const rolePanels = gamificationData?.role_panels || [];
  const activePanel = rolePanels.find((panel) => panel.role === activeBadgeRole) || rolePanels[0] || { badges: [], earned_count: 0, role_label: "Account Manager" };

  useEffect(() => {
    if (!rolePanels.length) return;
    if (!rolePanels.some((panel) => panel.role === activeBadgeRole)) {
      setActiveBadgeRole(rolePanels[0].role);
    }
  }, [activeBadgeRole, rolePanels]);

  return (
    <AppShell user={user} onLogout={onLogout}>
      <section className="space-y-4" data-testid="workspace-dashboard-root">
        <div className="page-header" data-testid="dashboard-page-header">
          <div>
            <h1 className="page-title" data-testid="dashboard-header-title">
              Welcome Back, <span style={{ color: "var(--brand-600)" }}>{user.name?.split(" ")[0] || "Arjun"}</span> 👋
            </h1>
            <p className="page-sub" data-testid="dashboard-header-subtitle">
              {now.header} · Week {now.weekNum}
            </p>
          </div>
        </div>

        <div className="kpi-grid" data-testid="dashboard-kpi-grid">
          <a href="/tasks" className="card kpi-tile" style={{ textDecoration: "none", color: "inherit" }} data-testid="dashboard-kpi-open-tasks">
            <div className="flex items-center justify-between">
              <p className="kpi-label">Open tasks</p>
              <span className="chip chip-mute">+{Math.min(3, openTasks.length)} today</span>
            </div>
            <p className="kpi-value" data-testid="dashboard-kpi-open-tasks-value">{openTasks.length}</p>
            <p className="kpi-sub">across {dashboardData.projects.length} projects</p>
            <div className="progress-bar" style={{ marginTop: 12 }} data-testid="dashboard-kpi-open-tasks-progress">
              <div style={{ display: "flex", height: "100%" }}>
                <div className="progress-fill" style={{ width: "33%", background: "var(--ok-500)" }} />
                <div className="progress-fill" style={{ width: "40%", background: "var(--warn-500)" }} />
                <div className="progress-fill" style={{ width: "27%", background: "var(--crit-500)" }} />
              </div>
            </div>
            <div className="flex gap-3 mt-2" style={{ fontSize: 11.5, color: "var(--mute-500)" }}>
              <span><span className="dot dot-ok" /> {Math.max(0, taskStats.onTrack)} Low</span>
              <span><span className="dot dot-warn" /> {taskStats.dueWeek} Med</span>
              <span><span className="dot dot-crit" /> {taskStats.overdue} High</span>
            </div>
          </a>

          <a href="/projects" className="card kpi-tile" style={{ textDecoration: "none", color: "inherit" }} data-testid="dashboard-kpi-projects">
            <div className="flex items-center justify-between">
              <p className="kpi-label">Active projects</p>
              <span className="chip chip-mute">Q2 retainer</span>
            </div>
            <p className="kpi-value" data-testid="dashboard-kpi-projects-value">{dashboardData.projects.length}</p>
            <p className="kpi-sub">{commandCenter.overview?.healthy_clients || 0} on track</p>
            <div className="flex mt-4" style={{ gap: 0 }} data-testid="dashboard-kpi-projects-avatars">
              {(commandCenter.manager_scorecards || []).slice(0, 5).map((manager) => (
                <span
                  key={manager.manager_email}
                  className="avatar avatar-sm avatar-a"
                  style={{ marginLeft: "-4px", outline: "2px solid #fff" }}
                  data-testid={`dashboard-kpi-manager-avatar-${manager.manager_email.replace(/[@.]/g, "-")}`}
                >
                  {initials(manager.manager_name)}
                </span>
              ))}
            </div>
          </a>

          <a href="/tasks" className="card kpi-tile crit-border" style={{ textDecoration: "none", color: "inherit" }} data-testid="dashboard-kpi-overdue">
            <div className="flex items-center justify-between">
              <p className="kpi-label" style={{ color: "var(--crit-700)", display: "flex", alignItems: "center", gap: 6 }}>
                <AlertTriangle className="h-3.5 w-3.5" style={{ color: "var(--crit-500)" }} /> Overdue tasks
              </p>
              <span className="chip chip-crit" style={{ background: "var(--crit-500)", color: "#fff" }}>Action needed</span>
            </div>
            <p className="kpi-value" style={{ color: "var(--crit-700)" }} data-testid="dashboard-kpi-overdue-value">{overdueTasks.length}</p>
            <p className="kpi-sub" style={{ color: "var(--crit-700)" }}>across {new Set(overdueTasks.map((task) => task.project_id)).size} projects</p>
            <ul style={{ marginTop: 10, fontSize: 12 }} data-testid="dashboard-kpi-overdue-list">
              {overdueTasks.slice(0, 3).map((task) => (
                <li key={task.id} className="flex justify-between" style={{ marginBottom: 4 }} data-testid={`dashboard-kpi-overdue-item-${task.id}`}>
                  <span className="truncate">{task.project_name?.split(" ")[0] || "Project"} — {task.task}</span>
                  <span className="tabular" style={{ color: "var(--crit-700)" }}>− {task.deadline}</span>
                </li>
              ))}
            </ul>
          </a>
        </div>

        <section className="gamification-context-strip" data-testid="dashboard-gamification-context-strip">
          <span data-testid="dashboard-gamification-context-copy">
            Viewing <strong>{activePanel.role_label || "Account Manager"}</strong> badge view for <strong>{user.name || user.email}</strong>.
          </span>
        </section>

        <section className="gamification-streak-section" data-testid="dashboard-gamification-streak-section">
          <div className="gamification-section-head" data-testid="dashboard-gamification-streak-head">
            <div>
              <h2 className="gamification-section-title" data-testid="dashboard-gamification-streak-title">🔥 Daily login streak</h2>
              <p className="gamification-section-sub" data-testid="dashboard-gamification-streak-subtitle">
                Working days only · weekends, holidays and approved leaves are skipped automatically.
              </p>
            </div>
            <button
              type="button"
              className="gamification-link-btn"
              onClick={() => navigate("/leaderboard")}
              data-testid="dashboard-gamification-open-leaderboard-button"
            >
              View leaderboard →
            </button>
          </div>

          <div className="gamification-streak-card" data-testid="dashboard-gamification-streak-card">
            <div className="gamification-streak-left" data-testid="dashboard-gamification-streak-left">
              <div className="gamification-streak-value" data-testid="dashboard-gamification-streak-value">{Number(streakSummary.current_streak || 0)}</div>
              <div className="gamification-streak-copy" data-testid="dashboard-gamification-streak-copy">
                <div className="gamification-streak-copy-title">day streak</div>
                <div className="gamification-streak-copy-sub">Logged in before 10:30 AM</div>
              </div>
            </div>

            <div className="gamification-streak-right" data-testid="dashboard-gamification-streak-right">
              <div className="gamification-week-grid" data-testid="dashboard-gamification-week-grid">
                {(streakSummary.week || []).map((day) => (
                  <div key={day.date} className="gamification-week-day" data-testid={`dashboard-gamification-week-day-${day.date}`}>
                    <span className="gamification-week-label" data-testid={`dashboard-gamification-week-label-${day.date}`}>{day.day_label}</span>
                    <span className={`gamification-week-badge ${day.state}`} data-testid={`dashboard-gamification-week-state-${day.date}`}>
                      {day.state === "done" ? "✓" : day.state === "today" ? "NOW" : day.state === "skip" ? "—" : "•"}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="gamification-streak-footer" data-testid="dashboard-gamification-streak-footer">
            {streakSummary.next_milestone ? (
              <span data-testid="dashboard-gamification-next-milestone-copy">
                🎁 {streakSummary.days_to_next_milestone || 0} day(s) to unlock {streakSummary.next_milestone}-day milestone badge.
              </span>
            ) : (
              <span data-testid="dashboard-gamification-next-milestone-copy">🎉 Highest milestone unlocked. Keep the streak alive!</span>
            )}
          </div>
        </section>

        <section id="monthly-badges" className="gamification-badge-section" data-testid="dashboard-gamification-badge-section">
          <div className="gamification-section-head" data-testid="dashboard-gamification-badge-head">
            <div>
              <h2 className="gamification-section-title" data-testid="dashboard-gamification-badge-title">🏅 Monthly badges</h2>
              <p className="gamification-section-sub" data-testid="dashboard-gamification-badge-subtitle">Resets every month · Last 12 months available</p>
            </div>
            <div className="gamification-role-switch" data-testid="dashboard-gamification-role-switch">
              {rolePanels.map((panel) => (
                <button
                  key={panel.role}
                  type="button"
                  className={`gamification-role-btn ${activeBadgeRole === panel.role ? "active" : ""}`}
                  onClick={() => setActiveBadgeRole(panel.role)}
                  data-testid={`dashboard-gamification-role-${panel.role}-button`}
                >
                  {panel.role_label}
                </button>
              ))}
            </div>
          </div>

          <div className="gamification-month-strip" data-testid="dashboard-gamification-month-strip">
            {monthOptions.map((option) => {
              const isActive = option.month === gamificationData?.month;
              const hasBadgeForRole = activeBadgeRole === "pod_owner" ? option.has_badge_pod_owner : option.has_badge_account_manager;
              return (
                <button
                  key={option.month}
                  type="button"
                  className={`gamification-month-pill ${isActive ? "active" : ""} ${hasBadgeForRole ? "has-badge" : ""}`}
                  onClick={() => loadGamificationMonth(option.month)}
                  data-testid={`dashboard-gamification-month-pill-${option.month}`}
                >
                  {option.label}
                </button>
              );
            })}
          </div>

          <div className="gamification-badge-grid" data-testid="dashboard-gamification-badge-grid">
            {(activePanel.badges || []).map((badge) => (
              <article
                key={badge.id}
                className={`gamification-badge-item ${badge.earned ? "earned" : "locked"}`}
                data-testid={`dashboard-gamification-badge-item-${badge.id}`}
              >
                <div className="gamification-badge-icon" data-testid={`dashboard-gamification-badge-icon-${badge.id}`}>
                  <span>{badge.emoji}</span>
                  {badge.earned ? <span className="gamification-badge-check" data-testid={`dashboard-gamification-badge-earned-${badge.id}`}>✓</span> : null}
                </div>
                <div className="gamification-badge-name" data-testid={`dashboard-gamification-badge-name-${badge.id}`}>{badge.name}</div>
                <p className="gamification-badge-desc" data-testid={`dashboard-gamification-badge-desc-${badge.id}`}>{badge.description}</p>
                {!badge.earned ? (
                  <div className="gamification-badge-progress" data-testid={`dashboard-gamification-badge-progress-${badge.id}`}>
                    <span style={{ width: `${Math.max(0, Math.min(100, Number(badge.progress || 0)))}%` }} />
                  </div>
                ) : null}
                <p className="gamification-badge-progress-label" data-testid={`dashboard-gamification-badge-progress-label-${badge.id}`}>
                  {badge.progress_label}
                </p>

                <div className="gamification-badge-tooltip" data-testid={`dashboard-gamification-badge-tooltip-${badge.id}`}>
                  <strong>{badge.tooltip_title}</strong>
                  <span>{badge.tooltip_body}</span>
                </div>
              </article>
            ))}
          </div>
        </section>

        <section className="jump-card p-4" data-testid="dashboard-filter-strip">
          <div className="mb-3 flex items-center gap-2 text-sm font-medium text-slate-700">
            <Filter className="h-4 w-4" />
            Filter projects / clients
          </div>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
            <Input
              value={filters.project}
              onChange={(event) => setFilters((prev) => ({ ...prev, project: event.target.value }))}
              placeholder="Filter by project"
              data-testid="dashboard-project-filter-input"
            />
            <Input
              value={filters.client}
              onChange={(event) => setFilters((prev) => ({ ...prev, client: event.target.value }))}
              placeholder="Filter by client"
              data-testid="dashboard-client-filter-input"
            />
            <Button onClick={() => loadDashboard(filters)} data-testid="dashboard-apply-filter-button">Apply Filters</Button>
            <Button
              variant="outline"
              onClick={() => {
                const reset = { project: "", client: "" };
                setFilters(reset);
                loadDashboard(reset);
              }}
              data-testid="dashboard-clear-filter-button"
            >
              Clear
            </Button>
          </div>
        </section>

        <section className="card" style={{ marginBottom: 20 }} data-testid="dashboard-agency-health-card">
          <div className="card-header">
            <div>
              <div className="card-title" data-testid="dashboard-agency-health-title">Agency health</div>
              <div className="card-sub" data-testid="dashboard-agency-health-subtitle">Operational (40) + Client Value (60) = Health (100)</div>
            </div>
            <span className="chip chip-mute" data-testid="dashboard-agency-health-total-clients">{commandCenter.overview?.total_clients || 0} clients</span>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))" }} data-testid="dashboard-agency-health-grid">
            {isLoading
              ? Array.from({ length: 4 }).map((_, index) => (
                <div key={`dashboard-health-skeleton-${index}`} style={{ padding: 20, borderRight: index === 3 ? "none" : "1px solid var(--paper-200)" }} data-testid={`dashboard-health-skeleton-${index}`}>
                  <div className="skeleton-shimmer" style={{ height: 12, width: "55%", marginBottom: 14 }} />
                  <div className="skeleton-shimmer" style={{ height: 30, width: "26%", marginBottom: 12 }} />
                  <div className="skeleton-shimmer" style={{ height: 10, width: "100%", marginBottom: 10 }} />
                  <div className="skeleton-shimmer" style={{ height: 10, width: "85%", marginBottom: 8 }} />
                  <div className="skeleton-shimmer" style={{ height: 10, width: "72%" }} />
                </div>
              ))
              : Object.entries(bandConfig).map(([key, cfg], index, arr) => {
              const rows = groupedBandRows[key] || [];
              const pct = Math.max(8, Math.round((rows.length / Math.max(1, commandCenter.overview?.total_clients || 1)) * 100));
              return (
                <div key={key} style={{ padding: 20, borderRight: index === arr.length - 1 ? "none" : "1px solid var(--paper-200)" }} data-testid={`dashboard-health-column-${key}`}>
                  <div className="flex items-center gap-2" style={{ fontSize: 12, color: cfg.textColor, fontWeight: 500 }}>
                    <span className={cfg.dotClass} />
                    {cfg.label}
                    <span style={{ color: "var(--mute-400)", fontWeight: 400 }}>{cfg.range}</span>
                  </div>
                  <div className="kpi-value" style={{ fontSize: 28, color: cfg.textColor }} data-testid={`dashboard-health-count-${key}`}>{rows.length}</div>
                  <div className="progress-bar" style={{ marginTop: 10 }} data-testid={`dashboard-health-progress-${key}`}>
                    <div className="progress-fill" style={{ width: `${pct}%`, background: cfg.stroke }} />
                  </div>
                  <div style={{ marginTop: 10 }} data-testid={`dashboard-health-client-list-${key}`}>
                    {rows.slice(0, 3).map((row) => (
                      <div key={row.client_id} className="flex justify-between" style={{ fontSize: 12 }}>
                        <span className="truncate" data-testid={`dashboard-health-client-name-${row.client_id}`}>{row.client_name}</span>
                        <span className="tabular" style={{ color: cfg.textColor }} data-testid={`dashboard-health-client-score-${row.client_id}`}>{row.health_score}</span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        <section className="card" style={{ marginBottom: 20 }} data-testid="dashboard-project-health-card">
          <div className="card-header">
            <div>
              <div className="card-title" data-testid="dashboard-project-health-title">Project health</div>
              <div className="card-sub" data-testid="dashboard-project-health-subtitle">Sorted by risk — critical first</div>
            </div>
            <Button variant="outline" onClick={() => loadDashboard(filters)} data-testid="dashboard-project-health-refresh-button">
              <RefreshCcw className="mr-2 h-4 w-4" /> Refresh
            </Button>
          </div>

          <table className="data-table" data-testid="dashboard-project-health-table">
              <thead>
                <tr>
                  <th>Project</th>
                  <th>Team</th>
                  <th className="text-right">Health</th>
                  <th className="text-right">Overdue</th>
                  <th>Trend (90d)</th>
                  <th>Last update</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  Array.from({ length: 4 }).map((_, rowIndex) => (
                    <tr key={`dashboard-project-skeleton-${rowIndex}`} data-testid={`dashboard-project-health-skeleton-${rowIndex}`}>
                      <td colSpan={7} className="py-3">
                        <div className="skeleton-shimmer" style={{ height: 34, borderRadius: 10 }} />
                      </td>
                    </tr>
                  ))
                ) : dashboardData.projects.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="h-20 text-center text-slate-500" data-testid="dashboard-project-health-empty">No projects found.</td>
                  </tr>
                ) : (
                  dashboardData.projects.map((project) => {
                    const clientRow = (commandCenter.client_rows || []).find((row) => row.client_name === project.client_name);
                    const healthScore = clientRow?.health_score || 0;
                    const healthStatus = clientRow?.health_status || "watch";
                    const team = clientRow?.account_managers || [];
                    const overdueCount = projectOverdueMap.get(project.id) || 0;
                    const points = trendMap[project.id] || sparklinePoints(healthScore);
                    const cfg = bandConfig[healthStatus] || bandConfig.watch;
                    const trendPath = trendPathFromPoints(points);
                    const lastPoint = points[points.length - 1] || 0;
                    const lastY = Number((22 - lastPoint / 4).toFixed(1));

                    return (
                      <tr key={project.id} onClick={() => navigate(`/project/${project.id}`)} className="cursor-pointer" data-testid={`dashboard-project-health-row-${project.id}`}>
                        <td data-testid={`dashboard-project-health-name-${project.id}`}>
                          <div className="flex items-center gap-2">
                            <div className="avatar avatar-sm avatar-a">{initials(project.client_name || "P")}</div>
                            <div>
                              <div style={{ fontWeight: 500 }}>{project.client_name}</div>
                              <div style={{ fontSize: 11.5, color: "var(--mute-500)" }}>{project.service_type}</div>
                            </div>
                          </div>
                        </td>
                        <td>
                          <div className="flex" style={{ gap: 0 }}>
                            {(team.length ? team : [project.project_admin_email]).slice(0, 4).map((member) => (
                              <div key={`${project.id}-${member}`} style={{ marginLeft: "-4px" }}>
                                <span className="avatar avatar-sm avatar-e" data-testid={`dashboard-project-health-team-${project.id}-${member.replace(/[@.]/g, "-")}`}>{initials(member)}</span>
                              </div>
                            ))}
                          </div>
                        </td>
                        <td className="text-right">
                          <span className={cfg.chipClass} data-testid={`dashboard-project-health-score-${project.id}`}>
                            <span className={cfg.dotClass} />{healthScore} {healthStatus.replace("_", " ")}
                          </span>
                        </td>
                        <td className={`text-right tabular-nums ${overdueCount > 0 ? "text-rose-700" : "text-slate-500"}`} data-testid={`dashboard-project-health-overdue-${project.id}`}>
                          {overdueCount}
                        </td>
                        <td>
                          <svg width="90" height="24" viewBox="0 0 90 24" data-testid={`dashboard-project-health-trend-${project.id}`}>
                            <path d={trendPath} stroke={cfg.stroke} fill="none" strokeWidth="1.5" />
                            <circle cx="88" cy={lastY} r="2.5" fill={cfg.stroke} />
                          </svg>
                        </td>
                        <td style={{ color: "var(--mute-500)" }}>2h ago</td>
                        <td className="text-right" style={{ color: "var(--mute-500)", fontSize: 12.5 }}><span>View →</span></td>
                      </tr>
                    );
                  })
                )}
              </tbody>
          </table>
        </section>

        <div className="grid grid-cols-1 gap-4 xl:grid-cols-2" data-testid="dashboard-bottom-cards-grid">
          <section className="card" data-testid="dashboard-activity-card">
            <div className="card-header"><div className="card-title" data-testid="dashboard-activity-title">Today's activity</div></div>
            <ul data-testid="dashboard-activity-list">
              {openTasks.slice(0, 4).map((task) => (
                <li key={task.id} className="flex items-center gap-3" style={{ padding: "12px 20px", borderBottom: "1px solid var(--paper-200)" }} data-testid={`dashboard-activity-item-${task.id}`}>
                  <span className="avatar avatar-sm avatar-c">{initials(task.assigned_to)}</span>
                  <div className="min-w-0 flex-1" style={{ fontSize: 12.5 }}>
                    <p className="truncate" data-testid={`dashboard-activity-item-title-${task.id}`}>
                      <b>{task.assigned_to}</b> updated <b>{task.task}</b>
                    </p>
                  </div>
                  <span style={{ fontSize: 11.5, color: "var(--mute-500)" }}>{task.deadline}</span>
                </li>
              ))}
            </ul>
          </section>

          <section className="card" data-testid="dashboard-upcoming-deliverables-card">
            <div className="card-header">
              <div className="card-title" data-testid="dashboard-upcoming-deliverables-title">Upcoming deliverables</div>
              <span style={{ fontSize: 11.5, color: "var(--mute-500)" }}>Next 7 days</span>
            </div>
            <ul data-testid="dashboard-upcoming-deliverables-list">
              {dueInWeekTasks.length === 0 ? (
                <li className="flex items-center gap-3" style={{ padding: "12px 20px", borderBottom: "1px solid var(--paper-200)", color: "var(--ok-700)" }} data-testid="dashboard-upcoming-deliverables-empty">No deliverables due this week.</li>
              ) : (
                upcomingDeliverables.map((task) => (
                  <li key={task.id} className="flex items-center gap-3" style={{ padding: "12px 20px", borderBottom: "1px solid var(--paper-200)" }} data-testid={`dashboard-upcoming-item-${task.id}`}>
                    <span className="dot dot-warn" />
                    <div style={{ flex: 1 }}>
                      <p className="truncate" style={{ fontSize: 13, fontWeight: 500 }}>{task.task}</p>
                      <p style={{ fontSize: 11.5, color: "var(--mute-500)" }}>{task.project_name}</p>
                    </div>
                    <span className="inline-flex items-center gap-1" style={{ fontSize: 12, color: "var(--warn-700)" }}><Clock3 className="h-3.5 w-3.5" />{task.deadline}</span>
                  </li>
                ))
              )}
            </ul>
          </section>
        </div>
      </section>
    </AppShell>
  );
}
